import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import RightTimeApi from "@/API";
import { isUsableBearerToken, readBearerTokenForHelpDetail } from "@/utils/helpDetailAuth";
import { parseSanitizedHtml, sanitizeHtmlDocument } from "@/utils/sanitizeHtml";
import "@/style/sass/_helpDetailPage.scss";

function extractHelpDetailBody(html: string): string {
  const trimmed = html.trim();
  const bodyMatch = trimmed.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return bodyMatch ? bodyMatch[1].trim() : trimmed;
}

export default function HelpDetailPage() {
  const { helpContentId } = useParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [detailHtml, setDetailHtml] = useState("");

  useEffect(() => {
    const id = Number(helpContentId ?? 0);
    if (!Number.isFinite(id) || id <= 0) {
      setErrorMessage("Invalid help content id.");
      return;
    }

    let cancelled = false;

    async function loadDetailAsync() {
      const token = readBearerTokenForHelpDetail();
      if (!token) {
        if (!cancelled) {
          setErrorMessage("Unable to load detailed help. Sign in on the main window and try again.");
        }
        return;
      }

      try {
        const response = await fetch(RightTimeApi.URL(`/Help/${id}/Detail`), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Request-Type": "API-Call",
            Referer: window.location.href,
          },
        });

        if (response.status === 401) {
          if (!cancelled) {
            setErrorMessage("Your session could not be used for detailed help. Refresh the main window and try again.");
          }
          return;
        }

        if (!response.ok) {
          if (!cancelled) {
            setErrorMessage(response.status === 404
              ? "Detailed help content was not found."
              : "Unable to load detailed help content.");
          }
          return;
        }

        const refreshedToken =
          response.headers.get("X-Refreshed-Token")
          || response.headers.get("X-Refrehed-Token");
        if (isUsableBearerToken(refreshedToken)) {
          window.sessionStorage.setItem("BearerToken", refreshedToken);
        }

        const html = sanitizeHtmlDocument(await response.text());
        if (cancelled) return;

        setDetailHtml(extractHelpDetailBody(html));
      } catch {
        if (!cancelled) {
          setErrorMessage("Unable to load detailed help content.");
        }
      }
    }

    void loadDetailAsync();

    return () => {
      cancelled = true;
    };
  }, [helpContentId]);

  if (detailHtml) {
    return (
      <div className="helpDetailPage">
        <div className="helpDetailPageContent">
          {parseSanitizedHtml(detailHtml)}
        </div>
      </div>
    );
  }

  if (!errorMessage) {
    return <div className="helpDetailLoading">Loading help...</div>;
  }

  return (
    <div className="helpDetailError">
      <p>{errorMessage}</p>
    </div>
  );
}
